import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTrash,
  faEdit,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faCheckSquare,
  faSquare,
  faFilter,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { getApiBaseUrl } from '../../services/api';
import './TrainingDataBrowser.css';

interface TrainingQA {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  status: string;
  created_at: string;
  training_group_id: string | null;
  context?: {
    source?: string;
  };
}

interface TrainingBatch {
  training_group_id: string;
  count: number;
  earliest_date: string;
  latest_date: string;
  categories: string[];
}

interface TrainingDataBrowserProps {
  clientId: string;
  onDataChange?: () => void;
}

const TrainingDataBrowser: React.FC<TrainingDataBrowserProps> = ({ clientId, onDataChange }) => {
  const [data, setData] = useState<TrainingQA[]>([]);
  const [batches, setBatches] = useState<TrainingBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ question: '', answer: '', category: '' });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'soft' | 'hard'>('soft');

  useEffect(() => {
    loadData();
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, currentPage, searchQuery, categoryFilter, batchFilter]);

  const loadData = async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
        status: 'approved'
      });

      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (batchFilter) params.append('batch_id', batchFilter);

      const response = await fetch(
        `${getApiBaseUrl()}/learned-qa/${clientId}/training-data?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to load training data');

      const result = await response.json();
      setData(result.items);
      setTotalPages(result.total_pages);
      setTotalCount(result.total);
    } catch (error: any) {
      console.error('Error loading training data:', error);
      toast.error(error.message || 'Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    if (!clientId) return;

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(
        `${getApiBaseUrl()}/learned-qa/${clientId}/batches`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to load batches');

      const result = await response.json();
      setBatches(result.batches || []);
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map(item => item.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(
        `${getApiBaseUrl()}/learned-qa/${clientId}/training-data/delete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            qa_ids: Array.from(selectedIds),
            permanent: deleteMode === 'hard'
          })
        }
      );

      if (!response.ok) throw new Error('Failed to delete items');

      const result = await response.json();
      toast.success(
        `Successfully deleted ${result.deleted_count} item(s)${deleteMode === 'soft' ? ' (can be restored)' : ''}`
      );

      setSelectedIds(new Set());
      setShowDeleteModal(false);
      loadData();
      if (onDataChange) onDataChange();
    } catch (error: any) {
      console.error('Error deleting items:', error);
      toast.error(error.message || 'Failed to delete items');
    }
  };

  const handleEdit = (item: TrainingQA) => {
    setEditingId(item.id);
    setEditForm({
      question: item.question,
      answer: item.answer,
      category: item.category || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(
        `${getApiBaseUrl()}/learned-qa/${clientId}/training-data/${editingId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editForm)
        }
      );

      if (!response.ok) throw new Error('Failed to update item');

      toast.success('Item updated successfully');
      setEditingId(null);
      loadData();
      if (onDataChange) onDataChange();
    } catch (error: any) {
      console.error('Error updating item:', error);
      toast.error(error.message || 'Failed to update item');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ question: '', answer: '', category: '' });
  };

  const uniqueCategories = Array.from(new Set(data.map(item => item.category).filter(Boolean)));

  const categoryDisplayNames: Record<string, string> = {
    embeddings: 'knowledge',
    knowledge_fallback: 'knowledge fallback',
  };

  if (loading && data.length === 0) {
    return (
      <div className="training-browser-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading training data...</p>
      </div>
    );
  }

  return (
    <div className="training-data-browser">
      {/* Header with Search and Filters */}
      <div className="browser-header">
        <div className="browser-title">
          <h2>Training Data</h2>
          <span className="data-count">{totalCount} items</span>
        </div>

        <div className="browser-controls">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search questions and answers..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => {
                  setSearchQuery('');
                  setCurrentPage(1);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          <button
            className={`btn-filter ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} />
            Filters
          </button>

          {selectedIds.size > 0 && (
            <button
              className="btn-delete"
              onClick={() => setShowDeleteModal(true)}
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map((cat) => (
                <option key={cat} value={cat || ''}>
                  {categoryDisplayNames[cat ?? ''] ?? (cat ?? 'Uncategorized').replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Batch</label>
            <select
              value={batchFilter}
              onChange={(e) => {
                setBatchFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Batches</option>
              {batches.map((batch) => (
                <option key={batch.training_group_id} value={batch.training_group_id}>
                  Batch {batch.training_group_id.slice(0, 8)} ({batch.count} items)
                </option>
              ))}
            </select>
          </div>

          {(categoryFilter || batchFilter) && (
            <button
              className="btn-clear-filters"
              onClick={() => {
                setCategoryFilter('');
                setBatchFilter('');
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Data Table */}
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <button
                  className="checkbox-btn"
                  onClick={handleSelectAll}
                  title={selectedIds.size === data.length ? 'Deselect all' : 'Select all'}
                >
                  <FontAwesomeIcon
                    icon={selectedIds.size === data.length ? faCheckSquare : faSquare}
                  />
                </button>
              </th>
              <th style={{ width: '35%' }}>Question</th>
              <th style={{ width: '35%' }}>Answer</th>
              <th style={{ width: '15%' }}>Category</th>
              <th style={{ width: '10%' }}>Source</th>
              <th style={{ width: '5%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  {searchQuery || categoryFilter || batchFilter
                    ? 'No training data matches your filters'
                    : 'No training data available'}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.id} className={selectedIds.has(item.id) ? 'selected' : ''}>
                  <td>
                    <button
                      className="checkbox-btn"
                      onClick={() => handleSelectOne(item.id)}
                    >
                      <FontAwesomeIcon
                        icon={selectedIds.has(item.id) ? faCheckSquare : faSquare}
                      />
                    </button>
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <textarea
                        value={editForm.question}
                        onChange={(e) =>
                          setEditForm({ ...editForm, question: e.target.value })
                        }
                        rows={3}
                      />
                    ) : (
                      <div className="text-cell">{item.question}</div>
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <textarea
                        value={editForm.answer}
                        onChange={(e) =>
                          setEditForm({ ...editForm, answer: e.target.value })
                        }
                        rows={3}
                      />
                    ) : (
                      <div className="text-cell">{item.answer}</div>
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        value={editForm.category}
                        onChange={(e) =>
                          setEditForm({ ...editForm, category: e.target.value })
                        }
                      />
                    ) : (
                      <span className="category-badge">{categoryDisplayNames[item.category ?? ''] ?? (item.category ?? 'N/A').replace(/_/g, ' ')}</span>
                    )}
                  </td>
                  <td>
                    <span className="source-badge">
                      {item.context?.source || 'manual'}
                    </span>
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <div className="edit-actions">
                        <button
                          className="btn-save"
                          onClick={handleSaveEdit}
                          title="Save changes"
                        >
                          ✓
                        </button>
                        <button
                          className="btn-cancel"
                          onClick={handleCancelEdit}
                          title="Cancel"
                        >
                          ✗
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(item)}
                        title="Edit item"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Previous
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Training Data</h3>
            <p>
              You are about to delete <strong>{selectedIds.size}</strong> item(s).
            </p>

            <div className="delete-mode-selector">
              <label className="radio-option">
                <input
                  type="radio"
                  checked={deleteMode === 'soft'}
                  onChange={() => setDeleteMode('soft')}
                />
                <div>
                  <strong>Soft Delete (Recommended)</strong>
                  <p>Items can be restored later if needed</p>
                </div>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  checked={deleteMode === 'hard'}
                  onChange={() => setDeleteMode('hard')}
                />
                <div>
                  <strong>Permanent Delete</strong>
                  <p>Items will be permanently removed</p>
                </div>
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDataBrowser;
